// src/pages/invite.js
// Invitation eller nulstilling: brugeren vælger selv sin adgangskode via et engangslink.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../auth';

const GREEN = '#00B861';
const MIN_PASSWORD_LENGTH = 10;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: #ffffff;
`;

const Box = styled.div`
  background: white;
  border: 1px solid #E5E5E5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  padding: 32px;
  width: 100%;
  max-width: 400px;
`;

const Logo = styled.img`
  height: 32px;
  display: block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: #171717;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const Sub = styled.p`
  color: #6B7280;
  font-size: 14px;
  margin-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #E5E5E5;
  border-radius: 12px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  transition: box-shadow 0.2s, border-color 0.2s;

  &::placeholder { color: #A3A3A3; }

  &:focus {
    outline: none;
    border-color: ${GREEN};
    box-shadow: 0 0 0 2px ${GREEN};
  }
`;

const Button = styled.button`
  background: ${GREEN};
  color: white;
  border: none;
  min-height: 52px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #008f4c; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const ErrorMessage = styled.p`
  color: #EF4444;
  margin-top: 16px;
  font-size: 14px;
  text-align: center;
`;

function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptLink } = useAuth();
  const [info, setInfo] = useState(undefined); // undefined = henter, null = ugyldigt
  const [loadError, setLoadError] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`/api/invite/${encodeURIComponent(token)}`)
      .then(res => setInfo(res.data))
      .catch(err => {
        setInfo(null);
        setLoadError(err.response?.data?.error || 'Linket kunne ikke åbnes.');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Adgangskoden skal være mindst ${MIN_PASSWORD_LENGTH} tegn`);
      return;
    }
    if (password !== repeat) {
      setError('De to adgangskoder er ikke ens');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await acceptLink(token, password);
      navigate('/calculator', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Noget gik galt. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const isReset = info?.purpose === 'reset';

  return (
    <Page>
      <Box>
        <Logo src="/Autobasen_logo.svg" alt="Autobasen Logo" />

        {info === undefined && <Sub>Henter…</Sub>}

        {info === null && (
          <>
            <Title>Linket virker ikke</Title>
            <Sub>{loadError}</Sub>
          </>
        )}

        {info && (
          <>
            <Title>{isReset ? 'Vælg ny adgangskode' : `Velkommen, ${info.name}`}</Title>
            <Sub>
              {isReset
                ? `Vælg en ny adgangskode til ${info.username}.`
                : `Vælg en adgangskode til ABBeregner. Du logger ind med ${info.username}.`}
            </Sub>
            <Form onSubmit={handleSubmit}>
              {/* Skjult brugernavn, så password managers gemmer det rigtige login */}
              <input type="email" autoComplete="username" value={info.username} readOnly hidden />
              <Input
                type="password"
                autoComplete="new-password"
                placeholder={`Adgangskode (min. ${MIN_PASSWORD_LENGTH} tegn)`}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
              />
              <Input
                type="password"
                autoComplete="new-password"
                placeholder="Gentag adgangskode"
                value={repeat}
                onChange={e => setRepeat(e.target.value)}
              />
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Gemmer…' : isReset ? 'Gem og log ind' : 'Opret og log ind'}
              </Button>
            </Form>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}
      </Box>
    </Page>
  );
}

export default Invite;
